<template>
  <section
    v-if="sprint"
    class="hygiene-watch"
    :style="{ background: 'var(--bg-elev)', border: '1px solid var(--border)' }"
    data-test="hygiene-watch"
  >
    <header class="hw-head">
      <div>
        <p class="hw-eyebrow">Hygiene watch · {{ scopeLabel ?? "active sprint" }}</p>
        <h2 class="hw-title" data-test="hygiene-watch-title">
          {{ sprint.sprint_name }}
        </h2>
        <p class="hw-meta" data-test="hygiene-watch-meta">
          <span v-if="audit && audit.mode !== 'off'">
            Compliance audit · <strong>{{ audit.mode === "apply" ? "applied" : "dry-run" }}</strong>
            · {{ audit.tickets_evaluated }} tickets evaluated
          </span>
          <span v-else>
            Compliance audit not run for this sprint — counts limited to missing-SP.
          </span>
        </p>
      </div>
      <RouterLink
        :to="{ name: 'sprint', params: { key: projectKey, id: sprint.sprint_id } }"
        class="hw-open"
        data-test="hygiene-watch-open"
      >
        Open scorecard →
      </RouterLink>
    </header>

    <div v-if="audit && audit.mode !== 'off'" class="hw-tiles">
      <div class="hw-tile">
        <span class="hw-tile-label">Tickets flagged</span>
        <span class="hw-tile-value" :class="toneFor(flaggedCount)">{{ flaggedCount }}</span>
      </div>
      <div class="hw-tile">
        <span class="hw-tile-label">Missing SP</span>
        <span class="hw-tile-value" :class="toneForRule(missingSpCount)">{{ missingSpCount }}</span>
      </div>
      <div class="hw-tile">
        <span class="hw-tile-label">Missing end date</span>
        <span class="hw-tile-value" :class="toneForRule(missingEndDateCount)">{{ missingEndDateCount }}</span>
      </div>
      <div class="hw-tile">
        <span class="hw-tile-label">Missing epic</span>
        <span class="hw-tile-value" :class="toneForRule(missingEpicCount)">{{ missingEpicCount }}</span>
      </div>
    </div>

    <div v-if="capitalization?.applicable" class="hw-capex" data-test="hygiene-capex">
      <div class="hw-capex-head">
        <span class="hw-tile-label">Capitalization · CapEx share</span>
        <span class="hw-capex-info" :title="capexTooltip" aria-label="How the two percentages differ" tabindex="0">&#9432;</span>
      </div>
      <div class="hw-capex-figures">
        <div class="hw-capex-fig">
          <span class="hw-capex-pct" data-test="capex-pct-count">{{ pctLabel(capitalization.by_count.pct) }}</span>
          <span class="hw-capex-sub">
            by ticket count · {{ capitalization.by_count.capex }}/{{ capitalization.by_count.total }} tickets
          </span>
        </div>
      </div>
      <p v-if="capexDataQuality" class="hw-capex-dq" data-test="capex-dq">{{ capexDataQuality }}</p>
      <div v-if="capexFixLinks.length" class="hw-capex-fix" data-test="capex-fix">
        <a
          v-for="l in capexFixLinks"
          :key="l.id"
          :href="l.url"
          :title="l.tip"
          target="_blank"
          rel="noopener noreferrer"
          class="hw-capex-fixlink"
          :data-test="`capex-fix-${l.id}`"
        >{{ l.label }} →</a>
      </div>
      <div v-if="opexEpics.length" class="hw-capex-opex" data-test="capex-opex-epics">
        <p class="hw-capex-opex-head">
          Classified non-capitalizable — {{ opexEpics.length }}
          {{ opexEpics.length === 1 ? "Epic" : "Epics" }}, {{ opexEpicTicketTotal }}
          {{ opexEpicTicketTotal === 1 ? "ticket" : "tickets" }}
          <span class="hw-capex-opex-hint">(review if any should be CapEx)</span>
        </p>
        <ul class="hw-capex-opex-list">
          <li v-for="e in opexEpics" :key="e.epic_key">
            <a
              :href="e.url"
              target="_blank"
              rel="noopener noreferrer"
              class="hw-capex-opex-link"
              :data-test="`capex-opex-epic-${e.epic_key}`"
            >{{ e.epic_key }} — {{ e.summary || "(no summary)" }}
              <span class="hw-capex-opex-count">
                {{ e.ticket_count }} {{ e.ticket_count === 1 ? "ticket" : "tickets" }} ({{ e.sp }} SP)
              </span></a>
          </li>
        </ul>
      </div>
    </div>

    <JqlFilters
      scope="sprint"
      :project-key="projectKey"
      :sprint-id="sprint.sprint_id"
      :sprint-name="sprint.sprint_name"
      :window-days="windowDays"
      :counts="counts"
    />
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { RouterLink } from "vue-router";
import JqlFilters from "@/components/JqlFilters.vue";
import { sprintFilterCounts } from "@/lib/quickFilterCounts";
import { buildIssueKeysUrl, buildJqlUrl, buildIssueUrl } from "@/lib/jiraJql";
import type { OpexEpic, SprintDetail } from "@/api/types";

const props = defineProps<{
  projectKey: string;
  sprint: SprintDetail | null | undefined;
  // Override the "· active sprint" eyebrow suffix (e.g. "rolling window").
  scopeLabel?: string;
}>();

const audit = computed(() => props.sprint?.compliance);
const counts = computed(() => sprintFilterCounts(props.sprint));
// A Kanban window has no real Jira sprint — its filters scope by recent
// activity. Undefined for real sprints (keeps the `sprint = <id>` clause).
const windowDays = computed<number | undefined>(() =>
  props.sprint?.board_type === "kanban"
    ? props.sprint?.metrics?.throughput?.window_days ?? 14
    : undefined,
);

function num(v: number | null | undefined): number {
  return typeof v === "number" ? v : 0;
}

const flaggedCount = computed(() => num(counts.value["sprint-all"]));
const missingSpCount = computed(() => num(counts.value["sprint-missing-sp"]));
const missingEndDateCount = computed(() => num(counts.value["sprint-missing-end-date"]));
const missingEpicCount = computed(() => num(counts.value["sprint-missing-epic"]));

const capitalization = computed(() => props.sprint?.metrics?.capitalization);

const capexTooltip =
  "Capitalizable tickets ÷ all sprint tickets. A ticket is CapEx only when its " +
  "parent Epic's Capatizable flag is \"Yes\". No Epic, Epic=No, and " +
  "unclassified-Epic tickets all count as non-capitalizable (OpEx).";

function pctLabel(p: number | null | undefined): string {
  return typeof p === "number" ? `${Math.round(p * 100)}%` : "—";
}

const capexDataQuality = computed(() => {
  const c = capitalization.value;
  if (!c) return "";
  const parts: string[] = [];
  if (c.no_epic_count > 0) parts.push(`${c.no_epic_count} without an Epic`);
  if (c.unclassified_epic_count > 0) parts.push(`${c.unclassified_epic_count} under an unclassified Epic`);
  if (parts.length === 0) return "";
  return `${parts.join(", ")} — all counted as OpEx. Fix these in Jira to lift CapEx.`;
});

// Label-free sprint/window scope, e.g. `project = "TEW" AND sprint = 9266`.
// This ALWAYS resolves in Jira — unlike the `labels = rushd-hygiene-*` presets,
// which only return rows when compliance tagging has run in "apply" mode and
// actually written the labels (often off — then the label filter is empty).
const scopeUrl = computed<string>(() => {
  const proj = `project = "${props.projectKey.replace(/"/g, '""')}"`;
  const s = props.sprint;
  if (!s) return buildJqlUrl(proj);
  if (windowDays.value != null) return buildJqlUrl(`${proj} AND updated >= -${windowDays.value}d`);
  return buildJqlUrl(`${proj} AND sprint = ${s.sprint_id}`);
});

// Ticket keys the compliance audit flagged for a given rule. Empty when the
// audit didn't run (mode "off") — the metric still knows the COUNT, but not
// which tickets, so we can't pinpoint them.
function flaggedKeys(ruleId: string): string[] {
  const actions = audit.value?.actions;
  if (!actions) return [];
  return actions
    .filter((a) => a.violations?.some((v) => v.rule_id === ruleId))
    .map((a) => a.key);
}

// Actionable deep links into Jira for the tickets dragging CapEx down.
//   • If the audit gives us the exact ticket keys → `issuekey in (...)`, a
//     precise link that resolves no matter the label / project-type state.
//   • Otherwise (compliance off, no per-ticket data) → fall back to the whole
//     sprint, labelled honestly so we never imply a pre-filtered list that
//     Jira would return empty.
const capexFixLinks = computed<{ id: string; label: string; url: string; tip: string }[]>(() => {
  const c = capitalization.value;
  if (!c || !props.sprint) return [];
  const out: { id: string; label: string; url: string; tip: string }[] = [];

  if (c.no_epic_count > 0) {
    const keys = flaggedKeys("missing-epic");
    out.push(keys.length
      ? {
          id: "missing-epic",
          label: `Fix ${keys.length} missing-Epic ticket${keys.length === 1 ? "" : "s"} in Jira`,
          url: buildIssueKeysUrl(keys),
          tip: "Opens exactly the tickets the bot flagged as having no parent Epic.",
        }
      : {
          id: "missing-epic",
          label: `Find ${c.no_epic_count} missing-Epic ticket${c.no_epic_count === 1 ? "" : "s"} in Jira`,
          url: scopeUrl.value,
          tip: "Hygiene labels aren't applied on this board, so Jira can't pre-filter to just these. Opens the full sprint — the unlinked ones are the Stories/Tasks/Bugs with no parent Epic.",
        });
  }

  if (c.unclassified_epic_count > 0) {
    const keys = flaggedKeys("epic-missing-capex");
    out.push(keys.length
      ? {
          id: "epic-missing-capex",
          label: `Classify ${keys.length} Epic${keys.length === 1 ? "" : "s"} in Jira`,
          url: buildIssueKeysUrl(keys),
          tip: "Opens the tickets whose parent Epic has no Capatizable value.",
        }
      : {
          id: "epic-missing-capex",
          label: `Find ${c.unclassified_epic_count} unclassified-Epic ticket${c.unclassified_epic_count === 1 ? "" : "s"} in Jira`,
          url: scopeUrl.value,
          tip: "Hygiene labels aren't applied on this board, so Jira can't pre-filter to just these. Opens the full sprint.",
        });
  }

  return out;
});

// Epics finance has classified Capatizable="No" — the legit OpEx remainder.
// Surfaced so a reviewer can audit each one and flip it in Jira if wrong.
interface OpexEpicRow extends OpexEpic { url: string; }
const opexEpics = computed<OpexEpicRow[]>(() =>
  (capitalization.value?.opex_epics ?? []).map((e) => ({
    ...e,
    url: buildIssueUrl(e.epic_key),
  })),
);
const opexEpicTicketTotal = computed(() =>
  opexEpics.value.reduce((sum, e) => sum + e.ticket_count, 0),
);

function toneFor(n: number): string {
  if (n === 0) return "tone-ok";
  return "tone-warn";
}
function toneForRule(n: number): string {
  if (n === 0) return "tone-ok";
  if (n >= 5) return "tone-bad";
  return "tone-warn";
}
</script>

<style scoped>
.hygiene-watch {
  border-radius: 10px;
  padding: 16px 18px;
  display: flex; flex-direction: column; gap: 14px;
}
.hw-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.hw-eyebrow {
  margin: 0; font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--fg-subtle, #8a8f9c);
}
.hw-title { margin: 4px 0 0; font-size: 18px; font-weight: 600; color: var(--fg, #0b0c10); }
.hw-meta { margin: 4px 0 0; font-size: 12px; color: var(--fg-muted, #525663); }
.hw-open {
  font-size: 13px; color: var(--accent, #4f46e5); text-decoration: none;
  white-space: nowrap;
}
.hw-open:hover { text-decoration: underline; }

.hw-tiles {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
}
.hw-tile {
  display: flex; flex-direction: column; gap: 2px;
  padding: 10px 12px;
  background: var(--bg-card, #fff);
  border: 1px solid var(--border, #e4e6eb);
  border-radius: 8px;
}
.hw-tile-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--fg-muted, #525663); }
.hw-tile-value { font-size: 22px; font-weight: 700; color: var(--fg, #0b0c10); }
.hw-tile-value.tone-ok   { color: #065f46; }
.hw-tile-value.tone-warn { color: #92400e; }
.hw-tile-value.tone-bad  { color: #991b1b; }

.hw-capex {
  padding: 12px 14px;
  background: var(--bg-card, #fff);
  border: 1px solid var(--border, #e4e6eb);
  border-radius: 8px;
  display: flex; flex-direction: column; gap: 8px;
}
.hw-capex-head { display: flex; align-items: center; gap: 6px; }
.hw-capex-info {
  cursor: help; font-size: 12px; color: var(--fg-muted, #525663);
  border-radius: 999px; line-height: 1;
}
.hw-capex-info:hover, .hw-capex-info:focus { color: var(--accent, #4f46e5); outline: none; }
.hw-capex-figures {
  display: grid; gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}
.hw-capex-fig { display: flex; flex-direction: column; gap: 2px; }
.hw-capex-pct { font-size: 24px; font-weight: 700; color: var(--fg, #0b0c10); }
.hw-capex-sub { font-size: 11px; color: var(--fg-muted, #525663); }
.hw-capex-dq {
  margin: 0; font-size: 12px; color: #92400e;
  background: #fffbeb; border: 1px solid #fcd34d; border-radius: 6px;
  padding: 6px 8px;
}
.hw-capex-fix { display: flex; flex-wrap: wrap; gap: 8px 16px; margin-top: 2px; }
.hw-capex-fixlink {
  font-size: 12px; font-weight: 600; color: var(--accent, #4f46e5);
  text-decoration: none;
}
.hw-capex-fixlink:hover { text-decoration: underline; }
.hw-capex-opex { display: flex; flex-direction: column; gap: 6px; margin-top: 2px; }
.hw-capex-opex-head { margin: 0; font-size: 12px; font-weight: 600; color: var(--fg, #0b0c10); }
.hw-capex-opex-hint { font-weight: 400; color: var(--fg-muted, #525663); }
.hw-capex-opex-list { margin: 0; padding-left: 0; list-style: none; display: flex; flex-direction: column; gap: 4px; }
.hw-capex-opex-list li { display: flex; flex-wrap: wrap; gap: 4px 8px; align-items: baseline; font-size: 12px; }
.hw-capex-opex-link { font-weight: 600; color: var(--accent, #4f46e5); text-decoration: none; }
.hw-capex-opex-link:hover { text-decoration: underline; }
.hw-capex-opex-count { color: var(--fg-muted, #525663); }
</style>
